import React from "react";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { localeOptions as defaultLocaleOptions } from "helpers/localeOptions";

// Searchable "Language (Country)" picker. Single dropdown; the user can type
// any part of the language name OR country name (or the ISO code) and the
// list filters in real time.
//
// Props
//   value           - selected locale object (or null)
//   onChange        - (locale | null) => void
//   options         - optional override of the locale list. Defaults to the
//                     curated set in helpers/localeOptions.js. Each entry
//                     should have { value, language, country, label,
//                     languageLabel, countryLabel }.
//   label           - field label, defaults to "Language and Country"
//   placeholder     - input placeholder
//   required        - shows a red asterisk in the label
//   disabled        - disable the field
//   helperText      - helper text under the field
//   sx              - MUI sx applied to the root Autocomplete
const filter = createFilterOptions({
	matchFrom: "any",
	stringify: (option) =>
		[
			option.label,
			option.languageLabel,
			option.countryLabel,
			option.language,
			option.country,
			option.value,
		]
			.filter(Boolean)
			.join(" "),
});

const LocalePicker = ({
	value,
	onChange,
	options = defaultLocaleOptions,
	label,
	placeholder,
	required = false,
	disabled = false,
	helperText,
	sx,
}) => {
	const { t } = useTranslation();
	const resolvedLabel = label ?? t("label.languageAndCountry");
	const resolvedPlaceholder =
		placeholder ?? t("placeholder.searchByLanguageOrCountry");
	return (
		<Autocomplete
			fullWidth
			disabled={disabled}
			options={options}
			value={value}
			onChange={(_, next) => onChange?.(next)}
			getOptionLabel={(option) => option?.label ?? ""}
			isOptionEqualToValue={(option, val) => option.value === val.value}
			filterOptions={filter}
			autoHighlight
			openOnFocus
			renderOption={(props, option) => (
				<li {...props} key={option.value}>
					<span style={{ flex: 1 }}>
						<strong>{option.languageLabel}</strong>
						<span style={{ color: "#666" }}> ({option.countryLabel})</span>
					</span>
					<span
						style={{
							marginLeft: 12,
							color: "#999",
							fontSize: 12,
							fontFamily:
								"ui-monospace, SFMono-Regular, Menlo, monospace",
						}}
					>
						{option.value}
					</span>
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={resolvedLabel}
					required={required}
					placeholder={resolvedPlaceholder}
					helperText={helperText}
				/>
			)}
			sx={sx}
		/>
	);
};

export default LocalePicker;
